import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Lock } from "lucide-react";
import type { Project } from "@/types/project";
import type { Milestone } from "@/hooks/useMilestones";
import { avatarTone, pct, shortDate } from "@/lib/format";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Health colors — bars are colored by project.health (red/yellow/green) so
// the timeline reads as a status view first. Priority intentionally
// dropped from the bar coloring; it lives on the Portfolio table where
// it's useful for sort/filter, not on the Gantt where status matters more.
// ---------------------------------------------------------------------------

const HEALTH_BAR: Record<string, { bar: string; fill: string; text: string }> = {
  red:    { bar: "bg-red-200",     fill: "bg-red-500",     text: "text-red-900 dark:text-red-300" },
  yellow: { bar: "bg-amber-200",   fill: "bg-amber-500",   text: "text-amber-900 dark:text-amber-300" },
  green:  { bar: "bg-emerald-200", fill: "bg-emerald-500", text: "text-emerald-900 dark:text-emerald-300" },
  // Completed work — brand-navy (the deep blue used by the "Key
  // Initiatives" view toggle on Timeline + Executive). NOT green:
  // Jim K's feedback (May 2026) was that emerald for in-progress-
  // and-on-track collided visually with emerald for done, so a
  // healthy active bar and a finished bar read the same. Matching
  // the brand-navy button tone keeps "complete" on the same color
  // axis as the brand's primary accent — finished work reads as
  // "this is the brand color of the system" rather than "this is
  // another status" — and is clearly distinct from emerald.
  complete: { bar: "bg-brand-navy-100 dark:bg-brand-navy-500/15", fill: "bg-brand-navy-600", text: "text-brand-navy-900 dark:text-brand-navy-300" },
  // No health set yet (e.g. requested / approved before kickoff). Neutral
  // slate so the bar is visible but signals nothing about status.
  none:   { bar: "bg-muted",   fill: "bg-slate-400",   text: "text-ink" },
};

/** Bar color key — driven by health, except that completed work renders
 *  in brand-navy (the 'complete' key) regardless of what its health
 *  was. Without this rule, a completed initiative with green health
 *  stayed green while a completed supporting project whose health was
 *  never set went grey — inconsistent for execs since both are "done."
 *  Done reads as done. Brand-navy was chosen over emerald per Jim K's
 *  feedback so in-progress-and-on-track no longer collides with
 *  shipped, and the color matches the "Key Initiatives" view toggle
 *  on Timeline + Executive — finished work reads as the brand color.
 *  Otherwise: red/yellow/green pass through; null / unknown / 'n_a'
 *  falls through to the neutral 'none' bar. */
function healthKey(
  h: string | null | undefined,
  status?: string | null | undefined,
): keyof typeof HEALTH_BAR {
  const s = (status ?? "").toLowerCase().trim();
  if (s === "complete" || s === "completed" || s === "done") return "complete";
  const k = (h ?? "").toLowerCase().trim();
  if (k === "red" || k === "yellow" || k === "green") return k;
  return "none";
}

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

function addMonths(d: Date, n: number): Date {
  const r = new Date(d); r.setMonth(r.getMonth() + n); return r;
}
function addDays(d: Date, n: number): Date {
  const r = new Date(d); r.setDate(r.getDate() + n); return r;
}
function monthStart(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function weekStart(d: Date): Date {
  const r = new Date(d); r.setDate(r.getDate() - r.getDay() + 1); return r; // Monday
}
function monthsBetween(a: Date, b: Date): number {
  return (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
}
function daysBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}
function toDate(iso?: string | null): Date | null {
  if (!iso) return null;
  const d = new Date(iso); return Number.isNaN(d.getTime()) ? null : d;
}
function initials(name?: string | null): string {
  if (!name) return "??";
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (name.slice(0, 2) || "??").toUpperCase();
}
function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

// ---------------------------------------------------------------------------
// Zoom types
// ---------------------------------------------------------------------------

export type ZoomLevel = "months" | "weeks" | "days";

export interface GanttProps {
  projects: Project[];
  rangeStart: Date;
  rangeEnd: Date;
  zoom?: ZoomLevel;
  milestones?: Milestone[];
  showMilestones?: boolean;
  /** Set of initiative IDs that are currently expanded. Initiatives
   *  in this set render a down-chevron and Timeline interleaves their
   *  child project rows below them. */
  expandedInitiatives?: Set<string>;
  /** Callback when the user clicks an initiative's chevron. */
  onToggleInitiative?: (id: string) => void;
  /** Set of IDs that should render indented as children of an
   *  initiative (entity_kind is still 'project'; this just signals
   *  the row needs hierarchy styling). */
  childRowIds?: Set<string>;
  /** Map of project id → upstream projects still blocking it (id + name).
   *  Rows in this map render a red "Blocked by" label at the bar's start.
   *  Only projects with an OPEN (not-yet-complete) blocker belong here. */
  blockedRows?: Map<string, { id: string; name: string }[]>;
  /** When true the chart opens scrolled to the range start (January)
   *  rather than auto-centering on today. */
  scrollToStart?: boolean;
  /** Project dependency edges (2026-09-14, Brett's Option B): drawn as
   *  elbow arrow connectors from the end of the blocker's bar to the
   *  start of the dependent's bar. Red when the dependent starts
   *  before its blocker ends. Rows without dates simply draw nothing —
   *  visual information only. */
  dependencies?: Array<{ blocker_project_id: string; blocked_project_id: string }>;
}
// ---------------------------------------------------------------------------
// Milestone display
// ---------------------------------------------------------------------------
// All milestones render the same neutral slate diamond — the bar already
// carries health-color signal for the project. Tone categorization is kept
// internally so we can still PRIORITIZE which milestones get visible
// space (overdue > due-soon > upcoming > complete) when the row is
// crowded, but the rendered color is uniform.

type MilestoneTone = "complete" | "overdue" | "soon" | "upcoming";

const MILESTONE_LABEL: Record<MilestoneTone, string> = {
  complete: "Complete",
  overdue:  "Overdue",
  soon:     "Due soon",
  upcoming: "Upcoming",
};

const MILESTONE_DIAMOND = { fill: "fill-ink-subtle", ring: "stroke-ink-3" };

function milestoneTone(m: Milestone, todayMs: number): MilestoneTone {
  if (m.completed_date || m.status === "complete" || m.status === "completed") return "complete";
  const due = toDate(m.due_date);
  if (!due) return "upcoming";
  const diffDays = (due.getTime() - todayMs) / 86400000;
  if (diffDays < 0) return "overdue";
  if (diffDays <= 7) return "soon";
  return "upcoming";
}

const TONE_PRIORITY: Record<MilestoneTone, number> = {
  overdue: 0,
  soon: 1,
  upcoming: 2,
  complete: 3,
};

/** Pick at most `cap` milestones to show: overdue + due-soon first, then nearest upcoming. */
function selectVisibleMilestones(
  list: Milestone[],
  todayMs: number,
  cap: number,
): { visible: Array<Milestone & { tone: MilestoneTone }>; hidden: number } {
  const annotated = list
    .filter((m) => !!m.due_date)
    .map((m) => ({ ...m, tone: milestoneTone(m, todayMs) }));
  annotated.sort((a, b) => {
    const tp = TONE_PRIORITY[a.tone] - TONE_PRIORITY[b.tone];
    if (tp !== 0) return tp;
    const ad = toDate(a.due_date)?.getTime() ?? 0;
    const bd = toDate(b.due_date)?.getTime() ?? 0;
    return ad - bd;
  });
  const visible = annotated.slice(0, cap);
  return { visible, hidden: Math.max(0, annotated.length - visible.length) };
}

// ---------------------------------------------------------------------------
// Column generation per zoom level
// ---------------------------------------------------------------------------

interface Column {
  date: Date;
  label: string;
  isHighlight: boolean; // Jan for months, Mon for weeks, 1st for days
  width: number;
}

function buildColumns(start: Date, end: Date, zoom: ZoomLevel): Column[] {
  const cols: Column[] = [];
  if (zoom === "months") {
    const total = monthsBetween(start, end) + 1;
    for (let i = 0; i < total; i++) {
      const d = addMonths(start, i);
      cols.push({
        date: d,
        label: d.toLocaleDateString("en-US", { month: "short" }),
        isHighlight: d.getMonth() === 0,
        width: 80,
      });
    }
  } else if (zoom === "weeks") {
    let d = weekStart(start);
    const endMs = end.getTime();
    while (d.getTime() <= endMs) {
      cols.push({
        date: new Date(d),
        label: `${d.getMonth() + 1}/${d.getDate()}`,
        isHighlight: d.getDate() <= 7, // first week of month
        width: 40,
      });
      d = addDays(d, 7);
    }
  } else {
    // days
    let d = new Date(start);
    const endMs = end.getTime();
    while (d.getTime() <= endMs) {
      const dow = d.getDay();
      cols.push({
        date: new Date(d),
        label: `${d.getDate()}`,
        isHighlight: d.getDate() === 1,
        width: 24,
      });
      d = addDays(d, 1);
    }
  }
  return cols;
}

/** Position a date on the column grid as a fractional offset in pixels. */
function dateToX(d: Date, columns: Column[], zoom: ZoomLevel): number {
  if (columns.length === 0) return 0;
  const startMs = columns[0].date.getTime();

  if (zoom === "months") {
    const whole = monthsBetween(monthStart(columns[0].date), monthStart(d));
    const dayFrac = (d.getDate() - 1) / daysInMonth(d.getFullYear(), d.getMonth());
    return (whole + dayFrac) * 80;
  }
  if (zoom === "weeks") {
    const days = daysBetween(columns[0].date, d);
    return (days / 7) * 40;
  }
  // days
  const days = daysBetween(columns[0].date, d);
  return days * 24;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function GanttChart({
  projects,
  rangeStart,
  rangeEnd,
  zoom = "months",
  milestones = [],
  showMilestones = true,
  expandedInitiatives,
  onToggleInitiative,
  childRowIds,
  blockedRows,
  scrollToStart,
  dependencies,
}: GanttProps) {
  const columns = useMemo(() => buildColumns(rangeStart, rangeEnd, zoom), [rangeStart, rangeEnd, zoom]);
  const totalWidth = columns.reduce((s, c) => s + c.width, 0);

  // Today line
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayMs = today.getTime();
  const todayX = dateToX(today, columns, zoom);
  const todayInRange = todayX >= 0 && todayX <= totalWidth;

  // Auto-scroll the chart so today sits ~15% from the left edge on mount
  // and whenever the time window changes (zoom switch / range change).
  // Without this the chart opens scrolled all the way left, leaving the
  // user staring at empty pre-history while today is off-screen right.
  //
  // When `scrollToStart` is set the chart instead opens at the range's
  // start (January) — used by the exec initiatives view, where the
  // full year matters more than centering on today.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (scrollToStart) { el.scrollLeft = 0; return; }
    if (!todayInRange) return;
    const target = Math.max(0, todayX - el.clientWidth * 0.15);
    el.scrollLeft = target;
  }, [todayX, totalWidth, todayInRange, zoom, scrollToStart]);

  // Group milestones by project_id once. ANY row with a due_date
  // becomes a diamond — phases (milestone_type='deliverable') AND
  // legacy named milestones (milestone_type='milestone' — e.g.
  // Kickoff, GA). The earlier 'hide legacy' rule was wrong: phases
  // are big buckets (Planning, Execution); legacy rows are point
  // events. Both have value as diamonds; they're not duplicates.
  // Rows without due_date are skipped — no diamond without a target.
  const milestonesByProject = useMemo(() => {
    const map = new Map<string, Milestone[]>();
    if (!showMilestones) return map;
    for (const m of milestones) {
      if (!m.due_date) continue;
      const projectId = m.project_id;
      if (!projectId) continue;
      const arr = map.get(projectId) ?? [];
      arr.push(m);
      map.set(projectId, arr);
    }
    return map;
  }, [milestones, showMilestones]);

  // Filter projects with valid dates. Two row classes are date-
  // tolerant — they keep their slot in the list even when dates are
  // missing, just without a bar drawn:
  //   1. Initiatives (entity_kind='initiative') — quarter-grained;
  //      sometimes have no q_start at all
  //   2. Child rows under an expanded initiative — many child
  //      projects have only an end_date set, no start_date
  // Everything else (top-level project rows on the regular Projects
  // view) still gets dropped when missing dates; that's the right
  // default for the working timeline.
  const rangeStartMs = rangeStart.getTime();
  const rangeEndMs = addMonths(rangeEnd, 1).getTime();
  const scheduled = projects
    .map((p) => {
      const s = toDate(p.start_date);
      const e = toDate(p.end_date);
      const isInitiative = p.entity_kind === "initiative";
      const isChild = childRowIds?.has(p.id) ?? false;
      if (!s && !e) {
        return (isInitiative || isChild)
          ? { project: p, start: null as Date | null, end: null as Date | null }
          : null;
      }
      // Partial dates: at least one side known. Lenient — keep the row
      // and render a small target bar at the known date. Out-of-range
      // single dates still drop for non-init/child rows.
      const probe = e ?? s;
      if (probe && (probe.getTime() < rangeStartMs || probe.getTime() > rangeEndMs)) {
        return (isInitiative || isChild)
          ? { project: p, start: null as Date | null, end: null as Date | null }
          : null;
      }
      return { project: p, start: s, end: e };
    })
    .filter((x): x is { project: Project; start: Date | null; end: Date | null } => x !== null);

  // ---- Dependency arrows (Option B, 2026-09-14) --------------------
  // X comes from the same dateToX the bars use; Y is MEASURED from the
  // rendered rows (offsetTop + half height) because rows can grow past
  // ROW_H when the left-column name wraps — an index*ROW_H guess would
  // misalign exactly on the long-named rows.
  const rowsRef = useRef<HTMLDivElement | null>(null);
  const [depArrows, setDepArrows] = useState<
    Array<{ key: string; d: string; conflict: boolean }>
  >([]);
  // `scheduled` is rebuilt on every render (deliberately unmemoized
  // upstream), so this effect re-runs each pass — the signature guard
  // keeps setState from firing unless the arrows actually changed,
  // which would otherwise loop the render (max update depth).
  const depSigRef = useRef("");
  const barXById = useMemo(() => {
    const m = new Map<string, { xs: number | null; xe: number | null }>();
    for (const { project, start, end } of scheduled) {
      m.set(project.id, {
        xs: start ? dateToX(start, columns, zoom) : null,
        xe: end ? dateToX(end, columns, zoom) : null,
      });
    }
    return m;
  }, [scheduled, columns, zoom]);

  useLayoutEffect(() => {
    if (!dependencies?.length || !rowsRef.current) {
      if (depSigRef.current !== "") {
        depSigRef.current = "";
        setDepArrows([]);
      }
      return;
    }
    const container = rowsRef.current;
    const rowY = (id: string): number | null => {
      const el = container.querySelector<HTMLElement>(
        `[data-gantt-row="${CSS.escape(id)}"]`,
      );
      if (!el) return null;
      return el.offsetTop + el.offsetHeight / 2;
    };
    const arrows: Array<{ key: string; d: string; conflict: boolean }> = [];
    for (const dep of dependencies) {
      const from = barXById.get(dep.blocker_project_id);
      const to = barXById.get(dep.blocked_project_id);
      if (from?.xe == null || to?.xs == null) continue;
      const y1 = rowY(dep.blocker_project_id);
      const y2 = rowY(dep.blocked_project_id);
      if (y1 == null || y2 == null) continue;
      const conflict = to.xs < from.xe;
      // Elbow routing. Forward case (dependent starts after the blocker
      // ends): out of the blocker's end, vertical, into the dependent's
      // start at bar height. Conflict case (the arrow must travel
      // BACKWARDS): running the return leg at bar height slices through
      // the dependent's own bar (Brett, 2026-09-14) — so route it in
      // the whitespace just above the dependent's row and drop down
      // onto the bar's start from above.
      let d: string;
      if (!conflict) {
        const midX = Math.max(from.xe + 8, to.xs - 8);
        d = `M ${from.xe} ${y1} H ${midX} V ${y2} H ${to.xs - 2}`;
      } else {
        const laneY = y2 + (y1 < y2 ? -18 : 18);
        d = `M ${from.xe} ${y1} H ${from.xe + 8} V ${laneY} H ${to.xs} V ${y2 + (y1 < y2 ? -11 : 11)}`;
      }
      arrows.push({
        key: `${dep.blocker_project_id}->${dep.blocked_project_id}`,
        d,
        conflict,
      });
    }
    const sig = JSON.stringify(arrows);
    if (sig !== depSigRef.current) {
      depSigRef.current = sig;
      setDepArrows(arrows);
    }
  });

  // LEFT_COL = name/PM column width; ROW_H = each project's row height.
  // Wider LEFT_COL means fewer names need to wrap; taller ROW_H gives the
  // line-clamp-2 wrap below somewhere to land without overlapping the bar.
  const LEFT_COL = 320;
  const ROW_H = 56;

  // Month group headers for weeks/days zoom
  const monthHeaders = useMemo(() => {
    if (zoom === "months") return null;
    const headers: { label: string; width: number }[] = [];
    let currentMonth = -1;
    let currentYear = -1;
    let currentWidth = 0;
    for (const col of columns) {
      const m = col.date.getMonth();
      const y = col.date.getFullYear();
      if (m !== currentMonth || y !== currentYear) {
        if (currentWidth > 0) headers.push({ label: `${new Date(currentYear, currentMonth).toLocaleDateString("en-US", { month: "short", year: "numeric" })}`, width: currentWidth });
        currentMonth = m;
        currentYear = y;
        currentWidth = col.width;
      } else {
        currentWidth += col.width;
      }
    }
    if (currentWidth > 0) headers.push({ label: `${new Date(currentYear, currentMonth).toLocaleDateString("en-US", { month: "short", year: "numeric" })}`, width: currentWidth });
    return headers;
  }, [columns, zoom]);

  return (
    <div className="rounded-xl border border-line bg-surface overflow-hidden">
      <div className="flex items-center justify-between border-b border-line-soft px-5 py-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
            Project Gantt
          </div>
          <div className="mt-0.5 text-xs text-ink-muted">
            {scheduled.length} scheduled projects · {zoom} view
          </div>
        </div>
        <Legend />
      </div>

      <div ref={scrollRef} className="overflow-x-auto">
        <div style={{ minWidth: LEFT_COL + totalWidth }}>
          {/* Month group header for weeks/days zoom */}
          {monthHeaders && (
            <div className="flex border-b border-line bg-raised">
              <div style={{ width: LEFT_COL }} className="sticky left-0 z-20 shrink-0 border-r border-line bg-raised" />
              <div className="flex">
                {monthHeaders.map((mh, i) => (
                  <div
                    key={i}
                    style={{ width: mh.width }}
                    className="border-r border-line px-1 py-1 text-[9px] font-semibold text-ink-3 text-center truncate"
                  >
                    {mh.label}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Column headers */}
          <div className="sticky top-0 z-10 flex border-b border-line bg-canvas">
            <div
              style={{ width: LEFT_COL }}
              className="sticky left-0 z-20 shrink-0 border-r border-line bg-canvas px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-ink-muted"
            >
              Project
            </div>
            <div className="flex">
              {columns.map((col, i) => (
                <div
                  key={i}
                  style={{ width: col.width }}
                  className={cn(
                    "flex items-center justify-center border-r border-line py-2 text-[10px]",
                    col.isHighlight ? "bg-raised font-semibold text-ink-2" : "bg-canvas text-ink-subtle",
                  )}
                >
                  {col.label}
                </div>
              ))}
            </div>
          </div>

          {/* Rows */}
          <div className="relative" ref={rowsRef}>
            {/* Dependency arrows — elbow connectors over the bar area
                (offset by LEFT_COL so x=0 is the chart's time origin).
                Slate when the order works, red when the dependent
                starts before its blocker ends. */}
            {depArrows.length > 0 && (
              <svg
                className="pointer-events-none absolute inset-y-0 z-10"
                style={{ left: LEFT_COL, width: totalWidth, height: "100%" }}
                data-testid="dep-arrows"
              >
                <defs>
                  <marker id="dep-arrow-slate" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6" markerHeight="6" orient="auto">
                    <path d="M0,0 L8,4 L0,8 z" fill="#94a3b8" />
                  </marker>
                  <marker id="dep-arrow-red" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6" markerHeight="6" orient="auto">
                    <path d="M0,0 L8,4 L0,8 z" fill="#ef4444" />
                  </marker>
                </defs>
                {depArrows.map((a) => (
                  <path
                    key={a.key}
                    d={a.d}
                    fill="none"
                    stroke={a.conflict ? "#ef4444" : "#94a3b8"}
                    strokeWidth="1.5"
                    strokeDasharray={a.conflict ? "4 3" : undefined}
                    markerEnd={a.conflict ? "url(#dep-arrow-red)" : "url(#dep-arrow-slate)"}
                  />
                ))}
              </svg>
            )}
            {todayInRange && (
              // The line was sized as `scheduled.length * ROW_H`, which
              // assumed every row was exactly ROW_H. Rows whose left-
              // column content wraps (long name + pills) grow taller,
              // and the fixed-height line came up short. inset-y-0
              // makes the line track the rows container's real height
              // no matter how rows lay out.
              <div
                className="pointer-events-none absolute inset-y-0 z-20"
                style={{ left: LEFT_COL + todayX }}
              >
                <div className="h-full w-px bg-red-400" />
                <div className="absolute -top-5 -translate-x-1/2 rounded bg-red-500 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white">
                  Today
                </div>
              </div>
            )}

            {scheduled.map(({ project, start, end }, i) => {
              const isInitiative = project.entity_kind === "initiative";
              // Pre-compute milestone visibility for this row so the
              // end-date label and the +N pill can reason about whether
              // a diamond is sitting at the bar's right edge (which is
              // where they all want to be at once — visual collision).
              const rowMilestones = showMilestones
                ? milestonesByProject.get(project.id) ?? []
                : [];
              const milestoneSplit = rowMilestones.length > 0
                ? selectVisibleMilestones(rowMilestones, todayMs, 3)
                : { visible: [], hidden: 0 };
              // All bars — projects, initiatives, quarter-derived
              // children — render with the same health-based colors
              // (red/yellow/green/none). Brett's call: visual
              // consistency across the chart. The fact that an
              // initiative is quarter-grained lives in the tooltip
              // ('quarter-derived from q_start/q_deliver'), not in
              // the bar styling.
              const hasBoth = start != null && end != null;
              // Partial: only one date known. Render a small target
              // bar (~28px wide) anchored to the known date. End-only
              // bars sit with their RIGHT edge at the date ("here's
              // the deadline"); start-only sit with LEFT edge at the
              // date ("started here, no end set yet").
              const partial = !hasBoth && (start != null || end != null);
              const hasBar = hasBoth || partial;
              const colors = HEALTH_BAR[healthKey(project.health, project.status)];
              const PARTIAL_W = 28;
              let x = 0;
              let width = 0;
              if (hasBoth) {
                x = Math.max(0, dateToX(start!, columns, zoom));
                const xEnd = Math.min(totalWidth, dateToX(end!, columns, zoom));
                width = Math.max(xEnd - x, 6);
              } else if (partial) {
                const probe = end ?? start!;
                const px = dateToX(probe, columns, zoom);
                width = PARTIAL_W;
                // End-only → right edge at probe (deadline);
                // start-only → left edge at probe (start point).
                x = end ? Math.max(0, px - width) : Math.max(0, px);
              }
              const completion = Math.min(Math.max(project.pct_complete, 0), 1);

              // Inline date labels — Smartsheet/MS Project pattern.
              const endLabel = end ? shortDate(project.end_date) : "";
              const startLabel = start ? shortDate(project.start_date) : "";
              // Detect a milestone diamond sitting at/near the bar's
              // right edge (within ~20px). When present, hide the
              // end-date text label — the diamond IS the marker, and
              // its hover popup already shows the date. Keeps the
              // bar-end visual clean instead of stacking diamond +
              // text + +N pill on top of each other.
              //
              // 20px was chosen empirically: in weeks view ~1 week,
              // in months view ~7 days. Catches "Go-Live milestone
              // at end_date" and also milestones a few days off
              // that still visually crowd the bar terminator.
              const barRightX = x + width;
              const hasEndMilestone = milestoneSplit.visible.some((m) => {
                const due = toDate(m.due_date);
                if (!due) return false;
                const mx = dateToX(due, columns, zoom);
                return Math.abs(mx - barRightX) < 20;
              });
              const showStartInsideBar = hasBoth && width >= 90;
              const fullSummary = hasBoth
                ? project.date_source === "rock_quarter"
                  ? `${project.name} · ${startLabel} → ${endLabel} · quarter-derived from rock_quarter`
                  : `${project.name} · ${startLabel} → ${endLabel} · ${pct(completion)}`
                : partial
                  ? end
                    ? `${project.name} · Due ${endLabel} (no start date)`
                    : `${project.name} · Started ${startLabel} (no end date)`
                  : `${project.name} · (no dates set)`;

              const isInit = project.entity_kind === "initiative";
              const isChild = childRowIds?.has(project.id) ?? false;
              const isExpanded = isInit && (expandedInitiatives?.has(project.id) ?? false);
              const navTarget = isInit
                ? `/initiatives/${project.id}`
                : `/portfolio/${project.id}`;

              return (
                <motion.div
                  key={project.id}
                  data-gantt-row={project.id}
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.25, delay: Math.min(i * 0.01, 0.15) }}
                  className="flex border-b border-line-soft hover:bg-canvas/80"
                >
                  <div
                    style={{ width: LEFT_COL }}
                    className={cn(
                      // z-20 keeps the sticky left column above bars,
                      // diamonds (z-10), and the +N pill (z-10) as
                      // the chart scrolls horizontally — without it,
                      // a milestone that lands at negative-x sneaks
                      // out behind the project-name pill.
                      "sticky left-0 z-20 flex shrink-0 items-center gap-2 border-r border-line bg-surface px-3 py-2",
                      // Child rows: hierarchy lives only in the left
                      // column via left-accent + indent. No row-wide
                      // background tint — the pastel health bars
                      // (emerald-200, red-200, etc.) got washed out
                      // against an indigo tint.
                      isChild && "border-l-2 border-l-brand-navy-200 pl-7",
                    )}
                  >
                    {/* Chevron for initiatives — only renders when the
                        parent provided the expand-control props. Acts
                        as the expand/collapse toggle. */}
                    {isInit && onToggleInitiative && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          onToggleInitiative(project.id);
                        }}
                        className="shrink-0 rounded p-0.5 text-ink-muted hover:bg-raised hover:text-ink-2"
                        title={isExpanded ? "Hide supporting projects" : "Show supporting projects"}
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5" />
                        )}
                      </button>
                    )}
                    {project.entity_kind === "initiative" ? (
                      // Initiative rows: show the SPONSOR's avatar in
                      // place of the PM's. Initiatives are governance-
                      // owned (sponsors are accountable), not PM-run,
                      // so the PM field was misleading on this row.
                      // Sponsor is the right identity anchor. Same
                      // circle shape as project rows keeps the
                      // left-column "person at the row anchor" pattern
                      // intact across mixed views.
                      project.sponsor ? (
                        <div
                          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ring-2 ring-brand-navy-200 dark:ring-brand-navy-500/30"
                          style={{ backgroundColor: avatarTone(project.sponsor) }}
                          title={`${project.sponsor} · Sponsor`}
                        >
                          {initials(project.sponsor)}
                        </div>
                      ) : (
                        // No sponsor set — same slate ID fallback as
                        // PM-less projects, but with the ring tweak so
                        // it still reads as initiative-vs-project at a
                        // glance.
                        <div
                          className="flex h-7 shrink-0 items-center rounded-md bg-raised px-2 text-[10px] font-bold tabular-nums text-ink-2 ring-2 ring-brand-navy-200 dark:ring-brand-navy-500/30"
                          title={`${project.id} · No sponsor assigned`}
                        >
                          {project.id}
                        </div>
                      )
                    ) : project.pm ? (
                      <div
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
                        style={{ backgroundColor: avatarTone(project.pm) }}
                        title={project.pm}
                      >
                        {initials(project.pm)}
                      </div>
                    ) : (
                      // No PM assigned — show the project ID in a slate
                      // pill instead of a colored avatar. Distinct shape
                      // (rounded rectangle vs. circle) signals "this is the
                      // project id, not a person" while still anchoring the
                      // row visually.
                      <div
                        className="flex h-7 shrink-0 items-center rounded-md bg-raised px-2 text-[10px] font-bold tabular-nums text-ink-2"
                        title={`${project.id} · No PM assigned`}
                      >
                        {project.id}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <Link
                        // Initiative rows route to /initiatives/:id —
                        // their ids are initiative ids (26-02, etc.),
                        // not project ids. Routing to /portfolio would
                        // 404 with 'project not found.'
                        to={
                          project.entity_kind === "initiative"
                            ? `/initiatives/${project.id}`
                            : `/portfolio/${project.id}`
                        }
                        title={project.name}
                        className="block text-xs font-medium leading-tight text-ink line-clamp-2 hover:text-brand-navy-700 dark:hover:text-brand-navy-300"
                      >
                        {project.name}
                      </Link>
                      <div className="flex items-center gap-1.5 text-[10px] text-ink-muted">
                        <span className="font-mono">{project.id}</span>
                        {(() => {
                          // Health pill — replaces the prior "· Red"
                          // text. Small, color-coded, lives inline in
                          // the metadata line so the eye picks up the
                          // health signal without scanning all the
                          // way over to the bar.
                          const raw = (project.health ?? "").toLowerCase().trim();
                          let label: string | null = null;
                          let pillCls = "";
                          if (raw === "n_a" || raw === "na" || raw === "n/a") {
                            label = "N/A";
                            pillCls = "bg-raised text-ink-muted";
                          } else if (raw === "red") {
                            label = "Red";
                            pillCls = "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300";
                          } else if (raw === "yellow") {
                            label = "Yellow";
                            pillCls = "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300";
                          } else if (raw === "green") {
                            label = "Green";
                            pillCls = "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300";
                          }
                          if (!label) return null;
                          return (
                            <span
                              className={`inline-flex items-center rounded-full px-1.5 py-px text-[9px] font-semibold leading-none ${pillCls}`}
                            >
                              {label}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  </div>

                  <div className="relative" style={{ width: totalWidth, height: ROW_H }}>
                    <div className="absolute inset-0 flex pointer-events-none">
                      {columns.map((col, j) => (
                        <div key={j} style={{ width: col.width }} className="h-full border-r border-line-soft" />
                      ))}
                    </div>

                    {/* No bar when the row is an initiative with no
                        q_start — Brett's call. Renders the row in the
                        left column only. Show a small "no dates" hint
                        inline so the row doesn't look broken. */}
                    {!hasBar && (
                      <div className="absolute inset-y-0 left-2 flex items-center text-[10px] italic text-ink-subtle">
                        {isInit ? "No quarter set" : "No dates set"}
                      </div>
                    )}

                    {/* The bar itself — only when we have dates.
                        Wrapped in a Link so clicking the bar goes
                        directly to the project / initiative detail
                        page. Mirrors the row-name link's target. */}
                    {hasBar && (
                      <>
                        <Link
                          to={navTarget}
                          className={cn(
                            "absolute top-1/2 -translate-y-1/2 block rounded-md ring-1 ring-inset ring-black/5 transition-shadow hover:ring-2 hover:ring-brand-navy-400 hover:shadow-md",
                            colors.bar,
                          )}
                          style={{
                            left: x,
                            width,
                            height: 22,
                          }}
                          title={fullSummary}
                        >
                          {hasBoth && (
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${completion * 100}%` }}
                              transition={{ duration: 0.7, delay: 0.2 + i * 0.01, ease: "easeOut" }}
                              className={cn("h-full rounded-md", colors.fill)}
                            />
                          )}
                          {hasBoth && (
                            <div className={cn(
                              "pointer-events-none absolute inset-0 flex items-center justify-between gap-1 px-2 text-[10px] font-semibold",
                              completion > 0.5 ? "text-white" : colors.text,
                            )}>
                              {showStartInsideBar ? (
                                <span className="tabular-nums opacity-80">{startLabel}</span>
                              ) : (
                                <span />
                              )}
                              {width > 40 && (
                                <span className="tabular-nums">{Math.round(completion * 100)}%</span>
                              )}
                            </div>
                          )}
                        </Link>

                        {/* Blocked marker — a red "Blocked by" label at
                            the bar's start when an upstream project
                            hasn't finished. Names the first blocker
                            inline; "+N" when there are more. Hover lists
                            them all. Visualize-only. */}
                        {(() => {
                          const blockedBy = blockedRows?.get(project.id);
                          if (!blockedBy || blockedBy.length === 0) return null;
                          const first = blockedBy[0];
                          const extra = blockedBy.length - 1;
                          return (
                            <div
                              className="absolute top-1/2 z-20 flex max-w-[260px] -translate-y-1/2 items-center gap-1 rounded-md bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold text-white shadow ring-2 ring-surface"
                              style={{ left: x }}
                              title={`Blocked by: ${blockedBy.map((b) => `${b.id} ${b.name}`).join(", ")}`}
                            >
                              <Lock className="h-2.5 w-2.5 shrink-0" />
                              <span className="shrink-0">Blocked by</span>
                              <span className="shrink-0 font-mono">{first.id}</span>
                              <span className="truncate">{first.name}</span>
                              {extra > 0 && <span className="shrink-0">+{extra}</span>}
                            </div>
                          );
                        })()}

                        {/* Date label outside the bar. For full bars,
                            shows end_date to the right. For partial
                            end-only, same. For partial start-only,
                            shows start_date to the right of the bar's
                            right edge (read direction). */}
                        {/* End-date text — hidden when a milestone
                            diamond is sitting at the bar's right edge
                            (the diamond's hover already shows the
                            date; rendering both stacks them visually). */}
                        {!hasEndMilestone && (
                          <div
                            className="pointer-events-none absolute top-1/2 -translate-y-1/2 whitespace-nowrap pl-1.5 text-[10px] font-semibold tabular-nums text-ink-2"
                            style={{ left: x + width }}
                          >
                            {hasBoth ? endLabel : partial ? (end ? endLabel : startLabel) : ""}
                          </div>
                        )}
                      </>
                    )}

                    {/* Milestone diamonds — uses the pre-computed
                        milestoneSplit so end-date suppression and the
                        diamond render reason about the same set. */}
                    {rowMilestones.length > 0 && (() => {
                      const { visible, hidden } = milestoneSplit;
                      return (
                        <>
                          {visible.map((m) => {
                            const due = toDate(m.due_date);
                            if (!due) return null;
                            const mx = dateToX(due, columns, zoom);
                            if (mx < -8 || mx > totalWidth + 8) return null;
                            const dueLabel = shortDate(m.due_date);
                            const toneLabel = MILESTONE_LABEL[m.tone];
                            return (
                              <div
                                key={m.id}
                                className="group absolute top-1/2 z-10 -translate-x-1/2 -translate-y-1/2"
                                style={{ left: mx }}
                              >
                                <svg
                                  width={14}
                                  height={14}
                                  viewBox="0 0 14 14"
                                  className="drop-shadow-sm cursor-default"
                                  aria-label={`Milestone: ${m.title}`}
                                >
                                  <polygon
                                    points="7,1 13,7 7,13 1,7"
                                    className={cn(MILESTONE_DIAMOND.fill, MILESTONE_DIAMOND.ring)}
                                    strokeWidth={1.5}
                                  />
                                </svg>
                                {/* Hover popup */}
                                <div className="pointer-events-none absolute bottom-full left-1/2 z-30 mb-1 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1.5 text-[10px] text-white shadow-lg group-hover:block">
                                  <div className="font-semibold">{m.title}</div>
                                  <div className="mt-0.5 flex items-center gap-1.5 text-ink-faint">
                                    <span className="tabular-nums">{dueLabel}</span>
                                    <span>·</span>
                                    <span>{toneLabel}</span>
                                    {m.owner && <><span>·</span><span>{m.owner}</span></>}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          {hidden > 0 && (
                            <Link
                              to={`/portfolio/${project.id}?tab=plan`}
                              className="absolute top-1/2 z-10 -translate-y-1/2 rounded-full bg-muted px-1.5 py-0.5 text-[9px] font-semibold text-ink-2 ring-1 ring-line-strong hover:bg-muted-strong"
                              // Offset depends on what else lives to
                              // the right of the bar: when end-date
                              // text is hidden (diamond at end), the
                              // pill can sit closer (12px past the
                              // diamond's right edge). When end-date
                              // is visible, push past it (~52px).
                              style={{ left: x + width + (hasEndMilestone ? 14 : 52) }}
                              title={`${hidden} more milestone${hidden === 1 ? "" : "s"}`}
                            >
                              +{hidden}
                            </Link>
                          )}
                        </>
                      );
                    })()}

                  </div>
                </motion.div>
              );
            })}

            {scheduled.length === 0 && (
              <div className="py-16 text-center text-sm text-ink-muted">
                No scheduled projects in this date range.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Legend() {
  return (
    <div className="flex items-center gap-4 text-[10px] text-ink-muted">
      <div className="flex items-center gap-3">
        {(["green", "yellow", "red"] as const).map((h) => (
          <div key={h} className="flex items-center gap-1.5">
            <div className={cn("h-2 w-4 rounded", HEALTH_BAR[h].fill)} />
            <span className="capitalize">{h}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5">
          <div className={cn("h-2 w-4 rounded", HEALTH_BAR.complete.fill)} />
          <span>Complete</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className={cn("h-2 w-4 rounded", HEALTH_BAR.none.fill)} />
          <span>No health</span>
        </div>
      </div>
      <div className="h-3 w-px bg-muted" />
      <div className="flex items-center gap-1.5">
        <svg width={10} height={10} viewBox="0 0 14 14">
          <polygon points="7,1 13,7 7,13 1,7" className={cn(MILESTONE_DIAMOND.fill, MILESTONE_DIAMOND.ring)} strokeWidth={1.5} />
        </svg>
        <span>Milestone</span>
      </div>
    </div>
  );
}
